import React from 'react';
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from 'react-router-dom';
import { BodyRegular } from '@shared/ui/atoms/fonts/Body';
import { Switch } from '@shared/ui/atoms/Switch';
import { selectCurrentUser, selectIsInTeams } from '@shared/store/auth.duck';
import { isPremiumTierUser } from '@shared/utils/AccessControl';
import TextField from '@shared/ui/atoms/TextField';
import IconButton from '@shared/ui/molecules/icon-button/IconButton';
import { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { WhereaboutsOptionItem } from "@features/whereabouts/services/WhereaboutOptions";
import styled from "styled-components";
import Colours from '@shared/ui/atoms/Colours';

export const MAX_OPTION_ITEM_LABEL_LENGTH = 64;

function key(path: string) {
  return `custom-whereabouts-page.${path}`;
}

export function OptionListSetting(props: Props) {
  const {t} = useTranslation();
  const {optionItems, onOptionItemsChanged} = props;
  const user = useSelector(selectCurrentUser);
  const isInTeams = useSelector(selectIsInTeams);
  const isTierRestricted = !isPremiumTierUser(user);

  const onItemLabelChanged = (index: number, label: string) => {
    const updated = optionItems.map((item, i) => i === index ? {...item, label: label.substring(0, MAX_OPTION_ITEM_LABEL_LENGTH)} : item);
    onOptionItemsChanged(updated);
  }

  const onRemoveItem = (index: number) => {
    onOptionItemsChanged(optionItems.filter((_, i) => i !== index));
  }

  const onAddItem = () => {
    onOptionItemsChanged([...optionItems, {label: ''}]);
  }

  return (
    <>
      <OptionRow>
        <Column>
          <BodyRegular weight={600}>{t(key('option-list-title'))}</BodyRegular>
          <BodyRegular>{t(key('option-list-description'))}</BodyRegular>
          {isTierRestricted && (
            isInTeams
              ? <BodyRegular style={{marginTop: 4, color: Colours.darkGrey}}>
                  {t(key('option-list-not-enabled'))}{' '}
                  <a href="mailto:support@team-today.com">support@team-today.com</a>
                </BodyRegular>
              : <BodyRegular style={{marginTop: 4, color: Colours.darkGrey}}>
                  {t(key('option-list-upsell'))}{' '}
                  <Link to="/manage/payments">{t(key('option-list-upsell-link'))}</Link>
                </BodyRegular>
          )}
        </Column>
        {/* A downgraded account that already enabled the list can still turn it off. */}
        <Switch value={props.optionListEnabled}
                disabled={props.disabled || (isTierRestricted && !props.optionListEnabled)}
                onChange={props.onOptionListEnabledChanged} />
      </OptionRow>

      {props.optionListEnabled && <OptionsContainer>
        {optionItems.map((item, index) => (
          <ItemRow key={item.id ?? `new-${index}`} data-test={`option-list-item-${index}`}>
            <TextField onChange={(value: string) => onItemLabelChanged(index, value)}
                       fullWidth={true}
                       label={''}
                       value={item.label}
                       disabled={props.disabled} />
            <IconButton icon={IconTypes.Bin}
                        onClick={() => onRemoveItem(index)}
                        disabled={props.disabled}
                        dataTest={`remove-option-list-item-${index}`}
                        style={{marginLeft: 8}} />
          </ItemRow>
        ))}

        <OutlineButton click={onAddItem}
                       text={key('option-list-add-option')}
                       dataTest={'add-option-list-item'}
                       disabled={props.disabled}
                       style={{marginBottom: 16}} />

        <OptionRow style={{marginLeft: 16}}>
          <Column>
            <BodyRegular weight={600}>{t(key('option-list-free-text-title'))}</BodyRegular>
            <BodyRegular>{t(key('option-list-free-text-description'))}</BodyRegular>
          </Column>
          <Switch value={props.freeTextEnabled} onChange={props.onFreeTextEnabledChanged} />
        </OptionRow>
      </OptionsContainer>}
    </>
  )
}

interface Props {
  optionListEnabled: boolean;
  onOptionListEnabledChanged: (value: boolean) => any;
  optionItems: WhereaboutsOptionItem[];
  onOptionItemsChanged: (items: WhereaboutsOptionItem[]) => any;
  freeTextEnabled: boolean;
  onFreeTextEnabledChanged: (value: boolean) => any;
  disabled?: boolean;
}

const Column = styled.div`
  max-width: 80%;
`

const OptionRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
`

const ItemRow = styled.div`
  display: flex;
  align-items: center;
  margin: 0 0 8px 16px;
`

const OptionsContainer = styled.div`
  border-left: 1px solid ${Colours.mildGrey};
  margin-left: 8px;
  margin-bottom: 16px;
  padding-left: 8px;
`
